/**
 * ZObject is a node that processes messages. It has inlets
 * and outlets, and the user can type in a number of function
 * types for it to run, along with a list of arguments.
 *
 * Inspired heaviliy from Max MSP's "object"
 *
 * Unlike midi effects, they do not need to be linked to a particular track
 *
 * Each function type is given a curried-function in the style of
 * other midi scripts (effects/midi/scripting). Their arguments
 * are fed in lazily
 */

import {uuid} from 'uuidv4';
import NodeMessaging from "messaging/NodeMessaging.js";
import Parser from './Parser.js';
import {doc} from './definitions/docs.js';
import {BANG} from "messaging/message-types.js"
import Dispatchable from "utils/Dispatchable.js"
import Preset from 'repos/Preset.js';
import {
    setBulkPresentationWindowPositions,
    setPresentationWindowPosition,
    setUXHideObject,
    setInletConnections,
    setLoadingSequence,
    setZObjectFlashing,
    setPluginsLoaded,
    setBulkWindowPositions,
    setEffectParameterValues} from 'actions/index.js';
import {replaceWindowPositions, replaceInletIds} from 'utils/replaceInletIds.js';
import ethereum from 'decentralized/ethereum.js';
import HasAttributes from "zobject/HasAttributes.js";
import HasActions from "zobject/HasActions.js";
import {ZOBJECT} from 'constants/object-types.js';
import {hotReloadNode} from "messaging/hotReload.js"
const presets = ethereum.presets;

export default class ZObject extends HasActions(HasAttributes(Dispatchable(NodeMessaging(Parser(class {}))))) {
    constructor(subSequence) {
        super();
        this.preset = new Preset();
        this.id = uuid();

        this.subSequence = subSequence;
        this.sequencerEngine = subSequence.sequencerEngine;
        this.sequencerEngine.idToObject[this.id] = this;

        this.counter=0;

        this.text = "";
        this.name = "";
        this.args = [];
        this.function = undefined;
        this.mutations = new Array(16).fill(false);

        this.window = {
            id: uuid()
        };

        this.newInlet("Input", (msg) => {
            this.onInputMessage(msg);
        });

        // as messages come in thru these "argument" inlets
        // we update the "args" list with their new valus

        this.newAttribute("primary-color");
        this.newAttribute("secondary-color");
        this.newAttribute("border-radius");
        this.newAttribute("Include in Presentation", false);

    }

    getObjectType() {
        return ZOBJECT;
    }

    getInstrumentType() {
        return "zobject." + this.name;
    }

    getAllParameters() {
        return this.effect ?
            this.effect.getParameters() :
            [];
    }

    set effect(effect) {
        this._effect = effect;
        let values = {};
        for (let p of effect.getParameters()) {
            values = {
                ... values,
                ... p.setStore(this.sequencerEngine.store)
            };
        }
        this.sequencerEngine.store.dispatch(
            setEffectParameterValues(values));
    }

    get effect() {
        return this._effect;
    }

    bang() {
        // send a bang message to the object function
        this.onInputMessage([BANG]);
    }

    getJson(isPreset) {
        let json = {
            id: this.id,
            text: this.text,
            width: this.width,
            height: this.height,
            attributes: this.attributes,
        };

        if (this.plugin) {
            json.plugin = this.plugin;
        }

        if (this.effect) {
            json.effect = this.effect && this.effect.getJson(isPreset);
        }
        if (this.height) {
            json.height = this.height;
        }
        if (this.width) {
            json.width = this.width;
        }

        if (this.mutations.some(x => x)) {
            let _mutations = [...this.mutations];
            _mutations.reverse();
            let index = _mutations.indexOf(true);
            json.mutations = this.mutations.slice(0, _mutations.length - index);
        }
        return json;
    }

    // isCopy means we want a COPY, so the IDS are not the same
    // as the json. So we don't end up w/ duplicate ids across
    // zobjects
    fromJson(json, isCopy=false, isInstance, isRoot, hide) {
        let idToId = {};
        if (!isCopy) {
            this.id = json.id;
            this.sequencerEngine.idToObject[this.id] = this;
        }

        idToId[json.id] = this.id;


        if (!isInstance) {
            this.text = json.text;
        }

        if (json.mutations) {
            this.dependencies = json.mutations;
            for (let i=this.dependencies.length; i < 16;i++) {
                this.dependencies.push(false);
            }
        }

        if (this.effect && json.effect) {
            idToId = {
                ... idToId,
                ... this.effect.fromJson(json.effect, isCopy, isInstance, isRoot, hide)
            };

            if (isCopy && !this.effect.zObjects) {
                this.effect.id = uuid();
                idToId[json.effect.id] = this.effect.id;
            }
            this.sequencerEngine.idToObject[this.effect.id] = this.effect;
        }
        if (json.width && json.height) {
            this.width = json.width;
            this.height = json.height;
        }
        if (json.attributes) {
            for (let name in json.attributes) {
                this.setAttribute(name, json.attributes[name]);
            }
        }

        if (json.plugin) {
            this.plugin = json.plugin;
        }

        return idToId;
    }

    /**
     * Returns true if it should delete the INPUT inlet (in
     * casess of inlets=0
     */
    onNewFunction(name, args) {
        this.name = name;

        // check the docs for how many outlets it contains
        let {outlets, isHot, inlets} = doc(name);
        this.isHot = isHot;

        // we pass the # of arguments to the outlets function (which
        // takes in # and returns the # of outlets for that func.)
        let numOutlets = 1;
        let useNames = false;
        if (outlets) {
            if (Array.isArray(outlets)) {
                numOutlets = outlets.length;
                useNames = true;
            } else {
                numOutlets = outlets(args.length);
            }
        }

        let oldOutlets = Object.keys(this.outlets).length;

        // now we adjust based on this
        if (oldOutlets > numOutlets) {
            // we have to delete some outlets
            for (let i=oldOutlets - 1; i > numOutlets - 1; i--) {
                // delete the ith outlet
                this.deleteOutlet(this.outputByNumber(i, true), false);
            }
            this.updateMessagingRedux();
        } else if (oldOutlets < numOutlets) {
            for (let i=oldOutlets; i < numOutlets; i++) {
                this.newOutlet(useNames ? outlets[i] : this.outputByNumber(i, true));
            }
        }

        // we pass the # of arguments to the outlets function (which
        // takes in # and returns the # of outlets for that func.)

        if (inlets !== undefined) {
            if (Array.isArray(inlets)) {
                // docs have specified names for each inlet
                // so we must delete all thhe currennt inlets
                // and initialize new inlets by name
                for (let inlet in this.inlets) {
                    if (!inlets.includes(inlet)) {
                        this.deleteInlet(inlet);
                    }
                }
                for (let inlet of inlets) {
                    if (this.inlets[inlet]) {
                        continue;
                    }
                    this.newInlet(inlet, (msg) => {
                        this.onInputMessage([inlet, ... msg]);
                    });
                }
            } else if (typeof inlets === "function") {
                inlets = Math.max(0, inlets(args.length));
                this.onNewArgs(new Array(Math.max(0, inlets - 1)).fill(0));
            } else {
                this.onNewArgs(new Array(Math.max(0, inlets - 1)).fill(0));
                if (inlets === 0) {
                    return true;
                }
            }
        }
   }

    // called when new arguments are written for a function
    onNewArgs(args) {
        let oldArgs = Object.keys(this.inlets);
        this.previousArgs = [... this.args];
        this.args = args;
        this.mutations = new Array(16).fill(false);

        if (oldArgs.length <= args.length){
            for (let i=oldArgs.length; i <= args.length; i++) {
                this.newInlet(i, (m) => this.onArg(i, m));
            }
        } else if (oldArgs.length > args.length) {
            for (let i=oldArgs.length; i > args.length; i--) {
                this.deleteInlet(i);
            }
        }
    }
 
    onArg(number, msg) {
        this.previousArgs = [... this.args]
        
        this.args[number-1] = msg[0];
        this.mutations[number-1] = true;

        if (this.isHot && this.lastMessageReceived !== undefined) {
            this.onInputMessage(this.lastMessageReceived);
        }
    }

    refresh() {
        if (this.sequencerEngine.isLoading) {
            return;
        }
        this.onInputMessage(this.lastMessageReceived || BANG);
    }

    set needsTick(x) {
        this._needsTick = x;
        this.sequencerEngine.tickingObjects.push(this);
    }

    get needsTick() {
        return this._needsTick;
    }

    arr = [];
    onInputMessage(msg) {
        if (this._disposed) {
            return;
        }
        if (!this.function) {
            return;
        }

        if (msg && msg[0] === "clearAllMessages") {
            console.log("clear all messages called");
            this.lastMessageReceived = 0;
            for (let i=0; i < this.args.length; i++) {
                this.args[i] = undefined;
            }
            return;
        }

        if (this.dependencies && this.isHot && !this.needsLoad) {
            for (let i=0; i < this.dependencies.length; i++) {
                if (this.dependencies[i] && !this.mutations[i]) {
                    // all dependencies have not filled yet
                    // so stop propagation
                    this.wasSuppressed = true;
                    //console.log("supressing=", this.name, this.subSequence.zObject && this.subSequence.zObject.args[0], msg);
                    /*
                    console.log(
                        'dependencies not met for object=%s sub=%s dep=%s mut=%s i=%s',
                        this.name,
                        this.subSequence.zObject &&
                            this.subSequence.zObject.args[0],
                        this.dependencies[i],
                        this.mutations[i],
                        i,
                        msg,
                        [... this.args]);
                        */
                    this.lastMessageReceived = msg;
                    return;
                }
            }
        }
        //if (window.zobjectDebug) {
            //console.log("on input message=", this.name, this.subSequence.zObject && this.subSequence.zObject.args[0], msg, this.counter++);
        //}

        //if (this.wasSuppressed) {
        /*
            console.log(
                'finally passed previously suppressed object=%s sub=%s', this.name, this.subSequence.zObject &&
                    this.subSequence.zObject.args[0], msg, [... this.args]);
                    */
            // this.wasSuppressed = false;
       // }

        if (!Array.isArray(msg)) {
            this.arr[0] = msg;
            msg = this.arr;
        }

        this.lastMessageReceived = msg;

        let msgOutputs = this.function(msg);
        if (msgOutputs !== undefined) {
            // the function didn't filter it out so we
            // send out the message
            if (!Array.isArray(msgOutputs)) {
                this.sendMessage(this.outputByNumber(0), msgOutputs);
                return;
            }

            for (let i=0 ; i < msgOutputs.length; i++ ){
                let msg = msgOutputs[i];
                if (msg !== undefined) {
                    let key = this.outputByNumber(i);
                    this.sendMessage(this.outputByNumber(i), msg);
                }
            }
        }
    }

    outputByNumber(i, force) {
        let key = "Output " + (i+1);
        if (this.outlets[key] || force) {
            return key;
        }
        let keys = Object.keys(this.outlets);
        return keys[i];
    }

    sendMessageToInlet(msg) {
        this.onInputMessage(msg);
    }

    loadPresetJson(json, isInstance=false, isRoot, hide) {
        let id = this.id;
        this.sequencerEngine.bulkConnections = {};
        this.sequencerEngine.dispatch(setLoadingSequence(this.sequencerEngine.sequenceId, true));
        let idToId = this.fromJson(json, true, isInstance, isRoot, hide);
        this.id = id;

        if (!json.windowPositions || !json.inletConnections) {
            this.sequencerEngine.dispatch(setLoadingSequence(this.sequencerEngine.sequenceId, false));
            return;
        }
        this.dispatch(
            setBulkWindowPositions(
                replaceWindowPositions(idToId, json.windowPositions)));

        if (json.presentationPositions) {
            this.dispatch(
                setBulkPresentationWindowPositions(
                    replaceWindowPositions(idToId, json.presentationPositions)));
        }

        this.sequencerEngine.checkSubPatchInstances(idToId);
        let inletConnections = replaceInletIds(
            idToId, json.inletConnections);
        this.sequencerEngine.loadInletConnectionsForId(
            inletConnections);

        this.sequencerEngine.checkInstanceConnections();

        // if its a sub-patch, then we need to "load bang" all the
        // objects that need to be started
        if (this.effect && this.effect.zObjects) {
            this.effect.loadBangZObjects();
        }

        this.sequencerEngine.dispatch(setLoadingSequence(this.sequencerEngine.sequenceId, false));

        for (let id in this.sequencerEngine.bulkConnections) {
            let connections = this.state.windows.inletConnections[id] || {};
            this.dispatch(
                setInletConnections(
                    id,
                    {
                            ... connections,
                        ... this.sequencerEngine.bulkConnections[id]
                    }));
        }

        console.log("FINISHED YALL");
        this.sequencerEngine.compile();

        this.sequencerEngine.groups.sendVolumes();
    }

    get position() {
        return this.state.windows.windowPositions[this.id];
    }

    delete() {
        console.log('delete called');
        if (this.isDeletable()) {
            console.log("trying to delete node");
            this.subSequence.deleteNode(this);
            if (this.effect && this.effect.dispose) {
                this.effect.dispose();
            }

            if (this.state.plugins.pluginsLoaded &&
                this.state.plugins.pluginsLoaded.includes(this)) {
                this.dispatch(
                    setPluginsLoaded(this.state.plugins.pluginsLoaded.filter(
                        x => x !== this)));
            }
        }
    }

    duplicate() {
        let json = this.getPresetJson();
        let {x,y} = this.state.windows.windowPositions[this.id];
        let width = this.width || 200;
        let height = this.width || 30;
        let zobject = this.subSequence.createZObject(x+width,y);
        zobject.loadPresetJson(json);

        let presentationPosition = this.state.windows.presentationWindowPositions[this.id];
        if (presentationPosition) {
            this.dispatch(setPresentationWindowPosition(zobject.id, {
                x: presentationPosition.x +width,
                y: presentationPosition.y
            }));
        }
    }

    async loadPreset(preset) {
        this.preset = new Preset(
            preset.title, preset.contentHash, preset.user, preset.blockNumber);
        let json = await presets.getPresetData(preset);
        this.loadPresetJson(json, undefined, true);
    }

    getPresetJson() {
        let json = this.getJson(true);

        let isSubPatch = this.effect && this.effect.zObjects;
        if (!isSubPatch) {
            return json;
        }

        // if this is a subpatch, we need to get:
        // 1. the window positions
        // 2. the inlet connections

        let windowPositions = {};
        let objects = this.getAllInnerObjects();

        objects.forEach(
            ({id}) => windowPositions[id] = this.state.windows.windowPositions[id]);
        let presentationPositions = {};
        objects.forEach(
            ({id}) => presentationPositions[id] = this.state.windows.presentationWindowPositions[id]);

        // now the inlet connections
        let inletConnections = this.state.windows.inletConnections[this.effect.id];

        // look for all nested sub-patches and add the inlet connctions of those to heree
        for (let object of objects) {
            if (object.effect && object.effect.zObjects) {
                // nest subpatch
                inletConnections = {
                    ... inletConnections,
                    ... this.state.windows.inletConnections[object.effect.id]
                };
            }
        }

        return {
            ... json,
            inletConnections,
            windowPositions,
            presentationPositions
        };
    }

    // Returns all zObjects and zMessages
    getAllInnerObjects() {
        if (this.effect && this.effect.zObjects) {
            let subpatch = this.effect;
            return [
                ... subpatch.zObjects,
                ... subpatch.zMessages,
                ... subpatch.zObjects.flatMap(
                    x => x.getAllInnerObjects())];
        }
        return [];
    }

    disposeMessaging() {
        this._disposed = true;
        if (this.function) {
            // this.function(["dispose"]);
        }
        super.disposeMessaging();
    }

    isDeletable() {
        if (this.effect && this.effect.isDeletable) {
            return this.effect.isDeletable();
        }

        return true;
    }

    flash() {
        let viewableSubPatches = this.state.zobject.viewableSubPatches;
        if (!this.subSequence.getRootSubSequence) {
            // then we flash cuz its main patch
        } else if (!viewableSubPatches || viewableSubPatches[viewableSubPatches.length-1] !== this.subSequence) {
        
            return;
        }
        if (this.subSequence.hasInstances) {
            return;
        }
        if (this.sequencerEngine.isLoading) {
            return;
        }
        this.dispatch(
            setZObjectFlashing(
                this.id,
                new Date().getTime()));
    }

    hotReload() {
        hotReloadNode(this);
    }

    hide() {
        this.dispatch(
            setUXHideObject(this.id, true));
    }

    get isHidden() {
        return this.state.spatial.hiddenObjects[this.id];
    }
}

